import { useEffect, useRef } from "react";
import TagManager from "react-gtm-module";
import { cp } from "../config";
import { COOKIE_TYPES } from "../enums/cookies";
import { gdprCookieHasValue } from "../utils/cookieUtils";

/*
 * GTM's snippet pushes a `gtm.start` entry as its first action, so its presence in the
 * dataLayer means GTM was already booted (e.g. by another mount of this component).
 */
const _isGtmRunning = (): boolean => window.dataLayer?.some((entry) => "gtm.start" in entry) ?? false;

/*
 * Boots Google Tag Manager exactly once, as soon as analytics consent exists. Lives at the
 * app root so GTM is available on every route — events fired before the GTM script loads
 * are queued in window.dataLayer (see useLogger) and picked up by GTM on boot.
 */
export default function GtmInitializer() {
    const hasAnalyticsConsent = gdprCookieHasValue(COOKIE_TYPES.ANALYTICS);

    const hasInitializedRef = useRef(false);

    useEffect(() => {
        // A missing REACT_APP_GTM_ID must not reach TagManager — react-gtm-module would
        // interpolate it into the script URL as "?id=undefined" and only warn.
        if (!hasAnalyticsConsent || !cp.cfg.REACT_APP_GTM_ID || hasInitializedRef.current || _isGtmRunning()) return;

        hasInitializedRef.current = true;
        TagManager.initialize({
            gtmId: cp.cfg.REACT_APP_GTM_ID,
            auth: cp.cfg.REACT_APP_GTM_AUTH,
        });
    }, [hasAnalyticsConsent]);

    return null;
}
